import React, { useCallback, useMemo, useState } from 'react'

import DateRangeIcon from '@mui/icons-material/DateRange'
import { Box, Button, Divider, Link, Popover, Stack, Typography } from '@mui/material'
import { DateCalendar, PickerDay, type PickerDayProps } from '@mui/x-date-pickers'
import { format as formatDate, isAfter, isSameDay, isWithinInterval, parseISO, startOfMonth, subMonths } from 'date-fns'

import { type CampaignDateRangePresetKey, campaignDateRangePresets, input } from '@albacross/frontend-utils'

export type DateRange = { from: string; to: string }

const PRESET_LABELS: Record<CampaignDateRangePresetKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  thisMonth: 'This month',
  thisYear: 'This year',
  past7Days: 'Past 7 days',
  past30Days: 'Past 30 days',
  previousWeek: 'Previous week',
  previousMonth: 'Previous month',
  lifetime: 'Lifetime',
}

const VALUE_FORMAT = 'yyyy-MM-dd'

// parseISO reads a date-only string as local midnight; `new Date(...)` reads it as UTC, which shifts
// both the label and the highlight by a day outside UTC. Calendar cells are local midnight.
export const formatRange = (range: DateRange) =>
  `${formatDate(parseISO(range.from), 'dd MMM')} – ${formatDate(parseISO(range.to), 'dd MMM')}`

type DateRangePickerProps = {
  value: DateRange | '' | null
  onChange: (value: DateRange | null) => void
  label?: string
}

const toRange = (from: Date, to: Date): DateRange =>
  isAfter(from, to)
    ? { from: formatDate(to, VALUE_FORMAT), to: formatDate(from, VALUE_FORMAT) }
    : { from: formatDate(from, VALUE_FORMAT), to: formatDate(to, VALUE_FORMAT) }

const DateRangePicker = ({ value, onChange, label }: DateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // A half-picked range is held locally so no request is fired between the two clicks.
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null)

  // formField hands down '' rather than null for an empty field, so normalise before anything reads it.
  const range = value || null

  const close = () => {
    setAnchorEl(null)
    setPendingFrom(null)
  }

  const commit = (next: DateRange | null) => {
    onChange(next)
    close()
  }

  const handleDayClick = (day: Date | null) => {
    if (!day) return
    if (!pendingFrom) {
      setPendingFrom(day)
      return
    }
    commit(toRange(pendingFrom, day))
  }

  const handlePreset = (key: CampaignDateRangePresetKey, from: Date, to: Date) =>
    // Lifetime as a literal 2000-01-01..today range would drop campaigns that have not started yet.
    commit(key === 'lifetime' ? null : toRange(from, to))

  const isSelected = useCallback(
    (day: Date) => {
      if (pendingFrom) return isSameDay(day, pendingFrom)
      if (!range) return false
      return isWithinInterval(day, { start: parseISO(range.from), end: parseISO(range.to) })
    },
    [pendingFrom, range],
  )

  // DayCalendar assigns `selected` after spreading slotProps, so only a slot component can drive the highlight.
  const renderDay = useCallback(
    (dayProps: PickerDayProps) => {
      const selected = isSelected(dayProps.day)
      return <PickerDay {...dayProps} aria-selected={selected} selected={selected} />
    },
    [isSelected],
  )

  const today = useMemo(() => new Date(), [])
  const months = useMemo(() => [startOfMonth(subMonths(today, 1)), startOfMonth(today)], [today])

  const buttonLabel = range ? formatRange(range) : 'All time'

  return (
    <>
      <Button
        aria-haspopup="true"
        fullWidth
        onClick={event => setAnchorEl(event.currentTarget)}
        startIcon={<DateRangeIcon />}
        sx={{ justifyContent: 'flex-start', height: '100%' }}
        variant="outlined">
        {label ? `${label}: ${buttonLabel}` : buttonLabel}
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={close}
        open={Boolean(anchorEl)}>
        <Stack direction="row">
          <Stack spacing={0.5} sx={{ p: 2, minWidth: 140 }}>
            {campaignDateRangePresets(today).map(({ key, from, to }) => (
              <Link
                align="left"
                component="button"
                key={key}
                onClick={() => handlePreset(key, from, to)}
                underline="hover"
                variant="body2">
                {PRESET_LABELS[key]}
              </Link>
            ))}
          </Stack>
          <Divider flexItem orientation="vertical" />
          <Box>
            {pendingFrom && (
              <Typography sx={{ px: 2, pt: 2 }} variant="caption">
                Pick the end of the range
              </Typography>
            )}
            <Stack direction="row">
              {months.map(month => (
                <DateCalendar
                  key={month.toISOString()}
                  onChange={handleDayClick}
                  referenceDate={month}
                  slots={{ day: renderDay }}
                  value={null}
                  views={['day']}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  )
}

export default input(null)(DateRangePicker)
